import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import PasswordInput from "@/components/ui/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import {
  AccountLinkingError,
  getLinkedProviders,
  linkEmailProvider,
  linkGoogleProvider,
  linkPhoneProvider,
  unlinkProvider,
} from "@/utils/accountLinking.native";
import { maskEmail, maskPhone } from "@/utils/maskIdentifier";
import {
  PhoneVerificationConfirmation,
  PhoneVerificationError,
} from "@/utils/phoneVerification.native";
import { Ionicons } from "@expo/vector-icons";
import { FirebaseAuthTypes } from "@react-native-firebase/auth";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  Modal,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import PhoneInput, {
  ICountry,
  isValidPhoneNumber,
} from "react-native-international-phone-number";

type ProviderKey = "password" | "google.com" | "phone";

const PROVIDERS: {
  key: ProviderKey;
  labelKey: string;
  icon: React.ComponentProps<typeof Ionicons>["name"];
}[] = [
  { key: "password", labelKey: "EMAIL", icon: "mail-outline" },
  { key: "google.com", labelKey: "GOOGLE", icon: "logo-google" },
  { key: "phone", labelKey: "PHONE_NUMBER", icon: "call-outline" },
];

const providerDisplayLabel = (
  user: FirebaseAuthTypes.User | null,
  key: ProviderKey,
): string | null => {
  if (!user) return null;
  const data = user.providerData.find((p) => p.providerId === key);
  if (!data) return null;
  if (key === "password") {
    const raw = data.email || user.email || null;
    return raw ? maskEmail(raw) : null;
  }
  if (key === "google.com") {
    return data.email ? maskEmail(data.email) : null;
  }
  if (key === "phone") {
    const raw = data.phoneNumber || user.phoneNumber || null;
    return raw ? maskPhone(raw) : null;
  }
  return null;
};

const SignInMethodsPage = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const nativeUser = user as FirebaseAuthTypes.User | null;

  const [providers, setProviders] = useState<string[]>(() =>
    nativeUser ? getLinkedProviders(nativeUser) : [],
  );
  const [busyProvider, setBusyProvider] = useState<ProviderKey | null>(null);

  const [linkEmailOpen, setLinkEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const [linkPhoneOpen, setLinkPhoneOpen] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<ICountry | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneStep, setPhoneStep] = useState<"enter-phone" | "enter-code">(
    "enter-phone",
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneVerification, setPhoneVerification] =
    useState<PhoneVerificationConfirmation | null>(null);
  const [phoneLoading, setPhoneLoading] = useState(false);

  const primaryProviderId = useMemo(
    () => nativeUser?.providerData[0]?.providerId ?? null,
    [nativeUser],
  );

  const refreshProviders = () => {
    if (nativeUser) {
      setProviders(getLinkedProviders(nativeUser));
    }
  };

  const showLinkError = (
    err: unknown,
    fallbackTitleKey: string,
    fallbackMessageKey: string,
  ) => {
    if (err instanceof AccountLinkingError) {
      switch (err.code) {
        case "USER_CANCELLED":
          return;
        case "CREDENTIAL_ALREADY_IN_USE":
          Alert.alert(
            t("CREDENTIAL_ALREADY_IN_USE_TITLE"),
            t("CREDENTIAL_ALREADY_IN_USE_MESSAGE"),
          );
          return;
        case "PROVIDER_ALREADY_LINKED":
          Alert.alert(t("ERROR"), t("PROVIDER_ALREADY_LINKED_MESSAGE"));
          return;
        case "REQUIRES_RECENT_LOGIN":
          Alert.alert(
            t("REQUIRES_RECENT_LOGIN_TITLE"),
            t("REQUIRES_RECENT_LOGIN_MESSAGE"),
            [
              { text: t("BACK"), style: "cancel" },
              { text: t("LOGOUT"), onPress: () => logout() },
            ],
          );
          return;
        case "ONLY_LINKED_PROVIDER":
          Alert.alert(t("ERROR"), t("UNLINK_ONLY_PROVIDER_HELPER"));
          return;
      }
    }
    Alert.alert(t(fallbackTitleKey), t(fallbackMessageKey));
  };

  const handleLinkGoogle = async () => {
    if (!nativeUser) return;
    setBusyProvider("google.com");
    try {
      await linkGoogleProvider(nativeUser);
      refreshProviders();
      Alert.alert(t("SUCCESS"), t("GOOGLE_LINK_SUCCESS"));
    } catch (err) {
      showLinkError(err, "ERROR", "GOOGLE_LINK_ERROR");
    } finally {
      setBusyProvider(null);
    }
  };

  const openLinkEmail = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setEmailError(null);
    setLinkEmailOpen(true);
  };

  const handleSubmitLinkEmail = async () => {
    if (!nativeUser) return;
    setEmailError(null);
    if (!email || !email.includes("@")) {
      setEmailError(t("PLEASE_ENTER_A_VALID_EMAIL"));
      return;
    }
    if (password.length < 6) {
      setEmailError(t("PASSWORD_MIN_LENGTH_ERROR"));
      return;
    }
    if (password !== confirmPassword) {
      setEmailError(t("PASSWORDS_DO_NOT_MATCH"));
      return;
    }

    setBusyProvider("password");
    try {
      await linkEmailProvider(nativeUser, email, password);
      refreshProviders();
      setLinkEmailOpen(false);
      Alert.alert(t("SUCCESS"), t("EMAIL_LINK_SUCCESS"));
    } catch (err) {
      showLinkError(err, "ERROR", "EMAIL_LINK_ERROR");
    } finally {
      setBusyProvider(null);
    }
  };

  const openLinkPhone = () => {
    setPhoneStep("enter-phone");
    setPhoneNumber("");
    setVerificationCode("");
    setPhoneVerification(null);
    setLinkPhoneOpen(true);
  };

  const handleSendPhoneCode = async () => {
    if (!nativeUser) return;
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber, phoneCountry)) {
      Alert.alert(t("ERROR"), t("PLEASE_ENTER_VALID_PHONE_NUMBER"));
      return;
    }
    setPhoneLoading(true);
    try {
      const prefix = phoneCountry?.idd.root || "";
      const confirmation = await linkPhoneProvider(
        nativeUser,
        `${prefix}${phoneNumber}`,
        { prefix, number: phoneNumber },
      );
      setPhoneVerification(confirmation);
      setPhoneStep("enter-code");
    } catch (err) {
      if (err instanceof PhoneVerificationError) {
        const messageKey =
          err.code === "INVALID_PHONE"
            ? "PLEASE_ENTER_VALID_PHONE_NUMBER"
            : err.code === "PHONE_ALREADY_IN_USE"
              ? "PHONE_ALREADY_IN_USE"
              : "FAILED_TO_SEND_CODE";
        Alert.alert(t("ERROR"), t(messageKey));
      } else {
        showLinkError(err, "ERROR", "PHONE_LINK_ERROR");
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneVerification || verificationCode.length < 6) {
      Alert.alert(t("ERROR"), t("PLEASE_ENTER_VERIFICATION_CODE"));
      return;
    }
    setPhoneLoading(true);
    try {
      await phoneVerification.confirm(verificationCode);
      refreshProviders();
      setLinkPhoneOpen(false);
      Alert.alert(t("SUCCESS"), t("PHONE_LINK_SUCCESS"));
    } catch (err) {
      if (err instanceof PhoneVerificationError) {
        const messageKey =
          err.code === "INVALID_CODE"
            ? "INVALID_VERIFICATION_CODE"
            : err.code === "CODE_EXPIRED"
              ? "CODE_EXPIRED_REQUEST_NEW"
              : err.code === "PHONE_ALREADY_IN_USE"
                ? "PHONE_ALREADY_IN_USE"
                : "VERIFICATION_FAILED";
        Alert.alert(t("VERIFICATION_FAILED"), t(messageKey));
      } else {
        Alert.alert(t("VERIFICATION_FAILED"), t("VERIFICATION_FAILED"));
      }
    } finally {
      setPhoneLoading(false);
    }
  };

  const labelKeyFor = (key: ProviderKey) =>
    PROVIDERS.find((p) => p.key === key)?.labelKey || "";

  const requestUnlink = (key: ProviderKey) => {
    if (!nativeUser) return;
    Alert.alert(
      t("UNLINK_PROVIDER_TITLE", { provider: t(labelKeyFor(key)) }),
      t("UNLINK_PROVIDER_MESSAGE", { provider: t(labelKeyFor(key)) }),
      [
        { text: t("BACK"), style: "cancel" },
        {
          text: t("UNLINK"),
          style: "destructive",
          onPress: async () => {
            setBusyProvider(key);
            try {
              await unlinkProvider(nativeUser, key);
              refreshProviders();
              Alert.alert(
                t("SUCCESS"),
                t("UNLINK_SUCCESS", { provider: t(labelKeyFor(key)) }),
              );
            } catch (err) {
              showLinkError(err, "ERROR", "UNLINK_ERROR");
            } finally {
              setBusyProvider(null);
            }
          },
        },
      ],
    );
  };

  return (
    <ThemedView style={{ flex: 1 }}>
      <CustomHeader
        title={t("SIGN_IN_METHODS")}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <ScrollView>
        <ThemedView style={styles.container}>
          <ThemedText style={styles.subtitle}>
            {t("SIGN_IN_METHODS_SUBTITLE")}
          </ThemedText>

          {PROVIDERS.map((row) => {
            const isLinked = providers.includes(row.key);
            const display = providerDisplayLabel(nativeUser, row.key);
            const isPrimary = primaryProviderId === row.key;
            const isOnly = providers.length <= 1;
            const isBusy = busyProvider === row.key;
            return (
              <ThemedView key={row.key} style={styles.row}>
                <ThemedView style={styles.rowLeft}>
                  <Ionicons name={row.icon} size={24} />
                  <ThemedView style={styles.rowText}>
                    <ThemedText style={styles.rowTitle}>
                      {t(row.labelKey)}
                      {isPrimary && isLinked ? (
                        <ThemedText style={styles.primaryTag}>
                          {"  "}
                          {t("PRIMARY_PROVIDER_LABEL")}
                        </ThemedText>
                      ) : null}
                    </ThemedText>
                    <ThemedText style={styles.rowSub}>
                      {isLinked ? display || t("LINKED") : t("NOT_LINKED")}
                    </ThemedText>
                    {isLinked && isOnly ? (
                      <ThemedText style={styles.helperText}>
                        {t("UNLINK_ONLY_PROVIDER_HELPER")}
                      </ThemedText>
                    ) : null}
                  </ThemedView>
                </ThemedView>
                <ThemedView style={styles.rowAction}>
                  {isBusy ? (
                    <ActivityIndicator />
                  ) : isLinked ? (
                    isOnly ? null : (
                      <Button
                        text={t("UNLINK")}
                        onPress={() => requestUnlink(row.key)}
                        style={[styles.actionBtn, styles.unlinkBtn]}
                        textStyle={styles.unlinkBtnText}
                      />
                    )
                  ) : (
                    <Button
                      text={t("LINK")}
                      onPress={() => {
                        if (row.key === "google.com") handleLinkGoogle();
                        else if (row.key === "password") openLinkEmail();
                        else if (row.key === "phone") openLinkPhone();
                      }}
                      style={styles.actionBtn}
                    />
                  )}
                </ThemedView>
              </ThemedView>
            );
          })}
        </ThemedView>
      </ScrollView>

      {/* Link email/password modal */}
      <Modal
        animationType="fade"
        transparent
        visible={linkEmailOpen}
        onRequestClose={() => setLinkEmailOpen(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <ThemedText style={styles.modalTitle}>
              {t("LINK_EMAIL_PASSWORD")}
            </ThemedText>
            <ThemedText style={styles.modalDescription}>
              {t("LINK_EMAIL_PASSWORD_DESCRIPTION")}
            </ThemedText>
            <TextInput
              style={styles.textInput}
              value={email}
              onChangeText={setEmail}
              placeholder={t("EMAIL")}
              placeholderTextColor="#888"
              autoCapitalize="none"
              keyboardType="email-address"
            />
            <PasswordInput value={password} onChangeText={setPassword} />
            <PasswordInput
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder={t("CONFIRM_PASSWORD")}
            />
            {emailError ? (
              <ThemedText style={styles.errorText}>{emailError}</ThemedText>
            ) : null}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalCancelBtn, styles.modalActionBtn]}
                onPress={() => setLinkEmailOpen(false)}
              >
                <ThemedText style={{ color: "#333" }}>{t("BACK")}</ThemedText>
              </TouchableOpacity>
              <Button
                text={
                  busyProvider === "password"
                    ? t("LINKING")
                    : t("LINK_ACCOUNT")
                }
                onPress={handleSubmitLinkEmail}
                disabled={busyProvider === "password"}
                style={[styles.modalActionBtn]}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Link phone modal */}
      <Modal
        animationType="fade"
        transparent
        visible={linkPhoneOpen}
        onRequestClose={() => setLinkPhoneOpen(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <ThemedText style={styles.modalTitle}>
              {t("LINK_PHONE_TITLE")}
            </ThemedText>
            <ThemedText style={styles.modalDescription}>
              {t("LINK_PHONE_DESCRIPTION")}
            </ThemedText>
            {phoneStep === "enter-phone" ? (
              <>
                <PhoneInput
                  value={phoneNumber}
                  onChangePhoneNumber={setPhoneNumber}
                  selectedCountry={phoneCountry}
                  onChangeSelectedCountry={setPhoneCountry}
                  defaultCountry="PY"
                />
                <View style={styles.modalActions}>
                  <TouchableOpacity
                    style={[styles.modalCancelBtn, styles.modalActionBtn]}
                    onPress={() => setLinkPhoneOpen(false)}
                  >
                    <ThemedText style={{ color: "#333" }}>
                      {t("BACK")}
                    </ThemedText>
                  </TouchableOpacity>
                  <Button
                    text={
                      phoneLoading
                        ? t("VERIFYING_CODE")
                        : t("PHONE_SIGNUP_SEND_CODE")
                    }
                    onPress={handleSendPhoneCode}
                    disabled={phoneLoading}
                    style={styles.modalActionBtn}
                  />
                </View>
              </>
            ) : (
              <>
                <ThemedText style={styles.modalDescription}>
                  {t("ENTER_VERIFICATION_CODE")}
                </ThemedText>
                <TextInput
                  style={styles.textInput}
                  value={verificationCode}
                  onChangeText={setVerificationCode}
                  keyboardType="numeric"
                  maxLength={6}
                  placeholder="123456"
                  placeholderTextColor="#888"
                />
                <View style={styles.modalActions}>
                  <TouchableOpacity
                    style={[styles.modalCancelBtn, styles.modalActionBtn]}
                    onPress={() => {
                      setPhoneStep("enter-phone");
                      setVerificationCode("");
                    }}
                  >
                    <ThemedText style={{ color: "#333" }}>
                      {t("BACK")}
                    </ThemedText>
                  </TouchableOpacity>
                  <Button
                    text={phoneLoading ? t("VERIFYING_CODE") : t("VERIFY")}
                    onPress={handleVerifyPhoneCode}
                    disabled={phoneLoading}
                    style={styles.modalActionBtn}
                  />
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
    </ThemedView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  subtitle: {
    fontSize: 14,
    opacity: 0.75,
    marginBottom: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingVertical: 16,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  rowLeft: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    flex: 1,
  },
  rowText: {
    flex: 1,
  },
  rowTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  primaryTag: {
    fontSize: 12,
    fontWeight: "500",
    color: "#0a7ea4",
  },
  rowSub: {
    fontSize: 13,
    opacity: 0.7,
    marginTop: 2,
  },
  helperText: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 4,
  },
  rowAction: {
    minWidth: 90,
    alignItems: "flex-end",
  },
  actionBtn: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  unlinkBtn: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#dc2626",
  },
  unlinkBtnText: {
    color: "#dc2626",
  },
  modalBackdrop: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  modalCard: {
    backgroundColor: "white",
    borderRadius: 20,
    padding: 25,
    width: "85%",
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 8,
  },
  modalDescription: {
    fontSize: 14,
    opacity: 0.75,
    marginBottom: 12,
  },
  textInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#fafafa",
    marginBottom: 12,
    width: "100%",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 13,
    marginTop: 4,
  },
  modalActions: {
    flexDirection: "row",
    gap: 10,
    marginTop: 16,
  },
  modalActionBtn: {
    flex: 1,
  },
  modalCancelBtn: {
    backgroundColor: "#e5e7eb",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 15,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default SignInMethodsPage;
